/* eslint-disable @typescript-eslint/no-shadow */
import { currentLimits } from '#~/api/billing/limits.js';
// Spec 4 (D1): the flag wait that gates the toolbar manifest.
import { beginRendererOperation, markRendererMilestone } from '#~/boot/renderer-startup.js';
import {
  useAnalytics,
  EVENT_CATEGORIES,
  EVENT_NAMES,
  MOVEMENT_TOOLS_PROPERTIES
} from '#~/analytics/index.js';
import { useHasOfflineDesktopBridge, useIsEmbeddedBridgeOnly } from '#~/agent/hooks.js';
import type { Command } from '#~/command/index.js';
import { Lang } from '#~/command/index.js';
import type { ToolsMap } from '#~/control/constants.js';
import { useToolVariants } from '#~/control/hooks.js';
import type { Tool } from '#~/control/index.js';
import { Tools, toTool } from '#~/control/index.js';
import { useHotKeys } from '#~/hot-keys/hooks.js';
import { toGlyphs, toString } from '#~/hot-keys/index.js';
import type { Combo } from '#~/hot-keys/index.js';
import { AutoTrackingTooltip } from './auto-tracking-tooltip.jsx';
import { Item } from './item.jsx';
import type { Props } from './types.js';
import * as DropdownMenu from '@studio/design-system/dropdown-menu';
import { Icon } from '@studio/design-system/icon';
import T from '@studio/design-system/themes';
import { useLatestFn } from '@studio/react-utils/hooks';
import { useOperatingSystem } from '@studio/react-utils/user-agent';
import type { T_Maybe } from '@tryiris-ai/js-types';
import { useEffect, useState } from 'react';
import type { ReactElement } from 'react';
import { twMerge } from 'cn';

/** `<Toolbar>` component. */
export const Toolbar = ({ available, selection, variant, ...props }: Props): ReactElement => {
  const hotKeys = useHotKeys();
  const os = useOperatingSystem();
  const { track } = useAnalytics();

  const [tools, setTools] = useState<ToolsMap>({} as ToolsMap);

  useEffect(() => {
    (async () => {
      // Spec 4 (D1): Tools() awaits the AUTOTRACKING flag before the WHOLE
      // toolbar manifest is published, so usable controls can lag a painted
      // shell. Measuring it is what distinguishes "the page is up" from "the
      // producer can actually do something" (review-4 §4, control usability).
      const closeTools = beginRendererOperation('toolbar.tools-manifest', 'control');
      try {
        const tools = await Tools();

        setTools(tools as ToolsMap);
        closeTools('ok');
        markRendererMilestone('toolbar.manifest-published');
      } catch (error) {
        closeTools('error');
        throw error;
      }
    })();
  }, []);

  const getGylphs = (_tool: Tool, command: Command): string[] => {
    const combo = (hotKeys as Record<Command, Combo>)[command] as T_Maybe<Combo>;

    return combo ? (toGlyphs(os, combo) ?? []) : [];
  };

  const handleValueChange = useLatestFn(async (nextValue: string) => {
    const nextTool = await toTool(nextValue);

    const tools = await Tools();

    if (nextTool) {
      const tool = tools[nextTool];

      if (tool.options.length > 0) {
        return;
      }

      selection.setValue(nextTool);

      // Track the tool selection event
      let eventName;
      switch (nextTool) {
        case 'joystick':
          eventName = EVENT_NAMES.CLICK_CURSOR_TOOL;
          break;
        case 'center':
          eventName = EVENT_NAMES.CLICK_CENTER_TOOL;
          break;
        case 'tracking':
          eventName = EVENT_NAMES.CLICK_AUTO_TRACKING;
          break;
        case 'fast-frame':
          eventName = EVENT_NAMES.CLICK_FAST_FRAME;
          break;
        default:
          return; // Don't track if not one of the specified tools
      }

      track(eventName, EVENT_CATEGORIES.MOVEMENT_TOOLS, {
        [MOVEMENT_TOOLS_PROPERTIES.TOOL_NAME]: nextTool
      });
    }
  });

  const [active, setActive] = useState(false);

  const [variants] = useToolVariants();
  const isEmbeddedBridgeOnly = useIsEmbeddedBridgeOnly();
  const hasOfflineDesktopBridge = useHasOfflineDesktopBridge();

  return (
    <div {...props} className='flex flex-row gap-2 bg-unset'>
      {available.map(({ disabled: initialDisabled, tool }) => {
        const { command, icon, options, hidden } = tools[tool] ?? {};

        if (hidden || command === undefined) {
          return null;
        }

        let label = Lang[command];

        const hasAutoTracking = currentLimits?.limits?.hasAutoTracking ?? false;
        const shouldShowFreePlanTooltip = !hasAutoTracking && tool === 'tracking';

        const shouldShowBridgeOfflineTooltip =
          hasAutoTracking && hasOfflineDesktopBridge && tool === 'tracking';

        const shouldShowEmbeddedOnlyTooltip =
          hasAutoTracking &&
          !hasOfflineDesktopBridge &&
          isEmbeddedBridgeOnly &&
          tool === 'tracking';

        const disabled =
          initialDisabled ||
          shouldShowFreePlanTooltip ||
          shouldShowBridgeOfflineTooltip ||
          shouldShowEmbeddedOnlyTooltip;

        // @ts-expect-error
        const combo = hotKeys[command];
        const shortcut = combo ? toString(os, combo) : undefined;

        let dynamicIcon = icon;

        const activeVariant = variants[tool];

        const option = options.find((opt) => opt.command === activeVariant);

        dynamicIcon = option ? option.icon : icon;

        if (options.length > 0) {
          return (
            <div key={label}>
              <DropdownMenu.Root>
                <Item
                  showDropdown
                  disabled={disabled}
                  icon={dynamicIcon}
                  onClick={() => {
                    if (disabled) {
                      return;
                    }

                    selection.setValue(tool);
                  }}
                  key={tool}
                  selected={tool === selection.value}
                  title={`${label}${shortcut ? ` (${shortcut})` : ''}`}
                  value={tool}
                />

                <DropdownMenu.Content
                  align='center'
                  data-theme={T.getTheme('dark')}
                  className={twMerge(
                    /* todo bg should be base-100 but is in light mode' */
                    'bg-base-100 border-0 text-[#F6F7F9]',
                    'mt-16 flex flex-col p-0'
                  )}
                  side='bottom'
                >
                  <div className='relative text-center'>
                    <div className='absolute left-[50%] top-[-10px] z-10 ml-[-10px]'>
                      <div
                        className={twMerge(
                          'bg-base-100 mx-auto h-[20px] w-[20px] origin-center rotate-45',
                          'transition-colors',
                          active && 'bg-primary'
                        )}
                      />
                    </div>
                  </div>
                  <div className='z-100'>
                    {options.map(({ command, icon, label }, index) => (
                      <DropdownMenu.Item
                        className='flex items-center bg-base-100 py-[22px] first:rounded-t first:border-t-0 last:rounded-b last:border-b-0'
                        icon={<Icon className='self-center text-xl' icon={icon} />}
                        key={command}
                        label={
                          <div className='flex w-[125px] flex-1 flex-row self-center'>
                            <span className='self-center'>{label}</span>
                            <div className='flex-1' />
                            <div className='flex flex-row items-center gap-6'>
                              {getGylphs(tool, command).map((glyph, index) => (
                                // eslint-disable-next-line tailwindcss/no-custom-classname
                                <span
                                  className={
                                    glyph.toLowerCase() === 'shift'
                                      ? 'border-text-primary rounded border-1 px-6 py-4 pt-[5px] text-[9px]'
                                      : ''
                                  }
                                  key={`${tool}_${command}_${index}_${glyph}`}
                                >
                                  {glyph}
                                </span>
                              ))}
                            </div>
                          </div>
                        }
                        onMouseEnter={
                          index === 0
                            ? (): void => {
                                setActive(true);
                              }
                            : undefined
                        }
                        onMouseLeave={
                          index === 0
                            ? (): void => {
                                setActive(false);
                              }
                            : undefined
                        }
                        onSelect={(): void => {
                          // ENG-4495 C4/D4: the dropdown submenu is a tool activation path. When the
                          // tool button is disabled (e.g. a magic tool on a driverless generic model)
                          // the submenu option must not activate it either — mirror the `disabled`
                          // guard the button `onClick` already applies above.
                          if (disabled) {
                            return;
                          }

                          selection.setValue(tool);
                          variant[1](tool, command);

                          // Track the tool variant selection
                          let eventName;
                          switch (tool) {
                            case 'center':
                              eventName =
                                command === 'tool.cine-center'
                                  ? EVENT_NAMES.CLICK_CINE_CENTER
                                  : EVENT_NAMES.CLICK_CENTER_TOOL;
                              break;
                            case 'fast-frame':
                              eventName =
                                command === 'tool.cine-frame'
                                  ? EVENT_NAMES.CLICK_CINE_FRAME
                                  : EVENT_NAMES.CLICK_FAST_FRAME;
                              break;
                            default:
                              return; // Don't track if not one of the specified tools
                          }

                          track(eventName, EVENT_CATEGORIES.MOVEMENT_TOOLS, {
                            [MOVEMENT_TOOLS_PROPERTIES.TOOL_NAME]: tool,
                            commandVariant: command
                          });
                        }}
                        style={/* hack to disable inline flex */ { display: 'flex' }}
                      />
                    ))}
                  </div>
                </DropdownMenu.Content>
              </DropdownMenu.Root>
            </div>
          );
        }

        const itemElement = icon && (
          <Item
            disabled={disabled}
            icon={icon}
            key={tool}
            onClick={(): void => {
              if (disabled) {
                return;
              }

              handleValueChange(tool);
            }}
            selected={tool === selection.value}
            showDropdown={options.length > 0}
            title={`${label}${shortcut ? ` (${shortcut})` : ''}`}
            value={tool}
          />
        );

        // Wrap tracking tool with conditional tooltip when disabled
        // Priority order: Free plan → Bridge offline → Embedded only → Available
        if (tool === 'tracking' && itemElement) {
          if (shouldShowFreePlanTooltip) {
            return <AutoTrackingTooltip key={tool} variant='free-plan' trigger={itemElement} />;
          }

          if (shouldShowBridgeOfflineTooltip) {
            return (
              <AutoTrackingTooltip key={tool} variant='bridge-offline' trigger={itemElement} />
            );
          }

          if (shouldShowEmbeddedOnlyTooltip) {
            return <AutoTrackingTooltip key={tool} variant='embedded-only' trigger={itemElement} />;
          }
        }

        return itemElement;
      })}
    </div>
  );
};
